import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  Sparkles,
  Send,
  ArrowLeft,
  Edit3,
  Save
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Textarea } from '../../ui/textarea';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { db } from '../../../lib/db';
import { formatDate } from '../../../lib/dateUtils';
import {
  formatStatus,
  getScoreColor,
  getScoreLabel,
  getStatusBadgeColor,
} from '../../../lib/calculations';
import { useAuthStore } from '../../../store/authStore';
import { useNotifications } from '../../../hooks/useDatabase';
import AIAnalysisSummary from '../../shared/AIAnalysisSummary';

const scoreLabels = {
  0: 'Weak',
  1: 'Needs Improvement',
  2: 'Below Expectations',
  3: 'Meets Expectations',
  4: 'Exceeds Expectations',
  5: 'Outstanding'
};

export default function CEOAppraisalReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { createNotification } = useNotifications();
  const [appraisal, setAppraisal] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [supervisor, setSupervisor] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [isSaving, setIsSaving] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [ceoComment, setCeoComment] = useState('');
  const [returnReason, setReturnReason] = useState('');
  
  // CEO score editing
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [editedFinalScore, setEditedFinalScore] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const idKey = isNaN(Number(id)) ? id : Number(id);
      const data = await db.appraisals.get(idKey) || await db.appraisals.get(String(id));
      if (!data) {
        toast.error('Appraisal not found');
        navigate(-1);
        return;
      }
      setAppraisal(data);
      setCeoComment(data.comments?.ceo || '');
      setEditedFinalScore(data.scores?.finalWeightedScore?.toFixed(1) || '');
      
      const emp = await db.users.get(data.userId);
      setEmployee(emp);
      
      if (data.managerId) {
        const sup = await db.users.get(data.managerId);
        setSupervisor(sup);
      }
    } catch (error) {
      console.error('Error loading appraisal', error);
      toast.error('Failed to load appraisal');
    }
  };

  const handleCeoCommentChange = (value) => {
    setCeoComment(value);
  };

  const handleSaveFinalScore = async () => {
    const scoreValue = parseFloat(editedFinalScore);
    if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 5) {
      toast.error('Score must be between 0 and 5');
      return;
    }
    try {
      setIsSaving(true);
      const updated = {
        ...appraisal,
        scores: { 
          ...(appraisal.scores || {}), 
          finalWeightedScore: scoreValue,
          ceoAdjustedScore: scoreValue,
          originalFinalScore: appraisal.scores?.finalWeightedScore
        },
        updatedAt: Date.now()
      };
      await db.appraisals.put(updated);
      setAppraisal(updated);
      setIsEditingScore(false);
      toast.success('Final score updated');
    } catch (error) {
      console.error('Error saving score', error);
      toast.error('Failed to save score');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCeoComment = async () => {
    try {
      setIsSaving(true);
      const updated = {
        ...appraisal,
        comments: { ...(appraisal.comments || {}), ceo: ceoComment },
        updatedAt: Date.now()
      };
      await db.appraisals.put(updated);
      setAppraisal(updated);
      toast.success('CEO comment saved');
    } catch (error) {
      console.error('Error saving comment', error);
      toast.error('Failed to save comment');
    } finally {
      setIsSaving(false);
    }
  };

  const handleFinalApproval = async () => {
    try {
      setIsSaving(true);
      const updated = {
        ...appraisal,
        status: 'finalized',
        ceoApprovedAt: Date.now(),
        ceoId: user?.id,
        comments: { ...(appraisal.comments || {}), ceo: ceoComment },
        finalizedAt: Date.now(),
        updatedAt: Date.now()
      };
      await db.appraisals.put(updated);
      setAppraisal(updated);
      toast.success('Appraisal approved and finalized!');
      setShowFinalizeModal(false);
    } catch (error) {
      console.error('Error finalizing appraisal', error);
      toast.error('Failed to finalize appraisal');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReturnToSupervisor = async () => {
    if (!returnReason.trim()) {
      toast.error('Please provide a reason for returning');
      return;
    }
    try {
      setIsSaving(true);
      const updated = {
        ...appraisal,
        status: 'manager_review',
        ceoReturnedAt: Date.now(),
        ceoReturnReason: returnReason,
        comments: { ...(appraisal.comments || {}), ceoReturn: returnReason },
        updatedAt: Date.now()
      };
      await db.appraisals.put(updated);
      setAppraisal(updated);
      
      // Send notification to supervisor
      if (appraisal.managerId) {
        await createNotification({
          userId: appraisal.managerId,
          type: 'appraisal_returned',
          title: 'Appraisal Returned by CEO',
          message: `The appraisal for ${employee?.profile?.firstName} ${employee?.profile?.lastName} has been returned. Reason: ${returnReason.slice(0, 100)}${returnReason.length > 100 ? '...' : ''}`,
          link: `/supervisor/appraisals/${appraisal.id}`
        });
      }
      
      toast.success('Appraisal returned to supervisor');
      setShowReturnModal(false);
      setReturnReason('');
    } catch (error) {
      console.error('Error returning appraisal', error);
      toast.error('Failed to return appraisal');
    } finally {
      setIsSaving(false);
    }
  };

  if (!appraisal) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  const isPendingCeoReview = appraisal.status === 'sent_to_ceo';
  const isFinalized = appraisal.status === 'finalized';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{appraisal.title}</h1>
            <Badge className={getStatusBadgeColor(appraisal.status)}>{formatStatus(appraisal.status)}</Badge>
          </div>
          <p className="text-gray-500 mt-1">Due: {formatDate(appraisal.dueDate)}</p>
          {employee && (
            <p className="text-sm text-gray-500 mt-1">
              Employee: {employee.profile?.firstName} {employee.profile?.lastName} • {employee.profile?.position}
            </p>
          )}
          {supervisor && (
            <p className="text-sm text-gray-500">
              Reviewed by: {supervisor.profile?.firstName} {supervisor.profile?.lastName}
            </p>
          )}
        </div>
        {isPendingCeoReview && (
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowReturnModal(true)} 
              disabled={isSaving}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Return to Supervisor
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={isSaving}
              onClick={() => setShowFinalizeModal(true)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" /> Approve & Finalize
            </Button>
          </div>
        )}
      </div>

      {/* AI Analysis Summary */}
      <AIAnalysisSummary appraisal={appraisal} />

      {/* Score Summary */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Employee Self Score</p>
              <p className={`text-3xl font-bold ${getScoreColor(appraisal.scores?.employeeSelfTotal)}`}>
                {appraisal.scores?.employeeSelfTotal?.toFixed(1) || 'N/A'}
              </p>
              <p className="text-xs text-gray-400 mt-1">{getScoreLabel(appraisal.scores?.employeeSelfTotal)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Manager Score</p>
              <p className={`text-3xl font-bold ${getScoreColor(appraisal.scores?.managerTotal)}`}>
                {appraisal.scores?.managerTotal?.toFixed(1) || 'N/A'}
              </p>
              <p className="text-xs text-gray-400 mt-1">{getScoreLabel(appraisal.scores?.managerTotal)}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Final Weighted Score</p>
              {isPendingCeoReview && isEditingScore ? (
                <div className="flex items-center justify-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={editedFinalScore}
                    onChange={(e) => setEditedFinalScore(e.target.value)}
                    className="w-20 text-center text-lg font-bold"
                  />
                  <Button size="icon" variant="ghost" onClick={handleSaveFinalScore} disabled={isSaving}>
                    <Save className="w-4 h-4 text-emerald-600" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <p className={`text-3xl font-bold ${getScoreColor(appraisal.scores?.finalWeightedScore)}`}>
                    {appraisal.scores?.finalWeightedScore?.toFixed(1) || 'N/A'}
                  </p>
                  {isPendingCeoReview && (
                    <Button size="icon" variant="ghost" onClick={() => setIsEditingScore(true)}>
                      <Edit3 className="w-4 h-4 text-gray-400 hover:text-indigo-600" />
                    </Button>
                  )}
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">{getScoreLabel(appraisal.scores?.finalWeightedScore)}</p>
              {appraisal.scores?.ceoAdjustedScore && (
                <p className="text-xs text-indigo-500 mt-1">
                  (CEO adjusted from {appraisal.scores?.originalFinalScore?.toFixed(1)})
                </p>
              )}
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Progress</p>
              <p className="text-3xl font-bold text-gray-900">{appraisal.progress?.overallPercent || 0}%</p>
              <Progress value={appraisal.progress?.overallPercent || 0} className="mt-2 h-2" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Supervisor Recommendation */}
      {appraisal.supervisorRecommendation && (
        <Card className="border-0 shadow-sm border-l-4 border-l-indigo-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-indigo-600" />
              Supervisor Recommendation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Recommendation Type</p>
                  <Badge className={`mt-1 ${
                    appraisal.supervisorRecommendation.type === 'promotion' ? 'bg-purple-100 text-purple-700' :
                    appraisal.supervisorRecommendation.type === 'salary_hike' ? 'bg-emerald-100 text-emerald-700' :
                    appraisal.supervisorRecommendation.type === 'retain' ? 'bg-blue-100 text-blue-700' :
                    appraisal.supervisorRecommendation.type === 'development' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {appraisal.supervisorRecommendation.type === 'promotion' && '📈 Recommend for Promotion'}
                    {appraisal.supervisorRecommendation.type === 'salary_hike' && '💰 Recommend for Salary Hike'}
                    {appraisal.supervisorRecommendation.type === 'retain' && '✓ Retain in Current Role'}
                    {appraisal.supervisorRecommendation.type === 'development' && '📚 Needs Development'}
                    {appraisal.supervisorRecommendation.type === 'pip' && '⚠️ Performance Improvement Plan'}
                  </Badge>
                </div>
                
                {appraisal.supervisorRecommendation.salaryHikePercentage > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Recommended Salary Hike</p>
                    <p className="text-lg font-semibold text-emerald-600">
                      {appraisal.supervisorRecommendation.salaryHikePercentage}%
                    </p>
                  </div>
                )}
                
                {appraisal.supervisorRecommendation.promotionRole && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Recommended New Role</p>
                    <p className="font-medium text-gray-900">
                      {appraisal.supervisorRecommendation.promotionRole}
                    </p>
                  </div>
                )}
              </div>
              
              <div>
                {appraisal.supervisorRecommendation.notes && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Justification</p>
                    <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                      {appraisal.supervisorRecommendation.notes}
                    </p>
                  </div>
                )}
                
                {appraisal.supervisorRecommendation.recommendedAt && (
                  <p className="text-xs text-gray-400 mt-2">
                    Submitted on {new Date(appraisal.supervisorRecommendation.recommendedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="performance">Performance Areas</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
          <TabsTrigger value="ceo">CEO Review</TabsTrigger>
        </TabsList>

        {/* Performance Areas Tab */}
        <TabsContent value="performance" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Performance Areas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {appraisal.performanceAreas?.map((area, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-gray-900">{area.competencyName}</h4>
                      <p className="text-sm text-gray-500">Weight: {area.weight}%</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Self</p>
                        <p className={`text-xl font-bold ${getScoreColor(area.employeeScore)}`}>
                          {area.employeeScore ?? 'N/A'}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500">Manager</p>
                        <p className={`text-xl font-bold ${getScoreColor(area.managerScore)}`}>
                          {area.managerScore ?? 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {area.employeeComment && (
                    <div className="mt-2 p-2 bg-white rounded border-l-2 border-blue-300">
                      <p className="text-xs text-gray-500 mb-1">Employee Comment:</p>
                      <p className="text-sm text-gray-700">{area.employeeComment}</p>
                    </div>
                  )}
                  {area.managerComment && (
                    <div className="mt-2 p-2 bg-white rounded border-l-2 border-violet-300">
                      <p className="text-xs text-gray-500 mb-1">Manager Comment:</p>
                      <p className="text-sm text-gray-700">{area.managerComment}</p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Current Goals</CardTitle>
              </CardHeader>
              <CardContent>
                {appraisal.goals?.current?.length > 0 ? (
                  <div className="space-y-3">
                    {appraisal.goals.current.map((goal, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <p className="font-medium text-gray-900">{goal.title}</p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              goal.addedBy === 'supervisor' 
                                ? 'bg-violet-50 text-violet-700 border-violet-200' 
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}
                          >
                            {goal.addedBy === 'supervisor' ? 'Supervisor' : 'Employee'}
                          </Badge>
                        </div>
                        {goal.description && <p className="text-sm text-gray-500 mt-1">{goal.description}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{goal.status || 'In Progress'}</Badge>
                          <Badge variant="outline" className="text-xs">{goal.priority || 'Medium'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No current goals</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg">Future Goals</CardTitle>
              </CardHeader>
              <CardContent>
                {appraisal.goals?.future?.length > 0 ? (
                  <div className="space-y-3">
                    {appraisal.goals.future.map((goal, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <p className="font-medium text-gray-900">{goal.title}</p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              goal.addedBy === 'supervisor' 
                                ? 'bg-violet-50 text-violet-700 border-violet-200' 
                                : 'bg-blue-50 text-blue-700 border-blue-200'
                            }`}
                          >
                            {goal.addedBy === 'supervisor' ? 'Supervisor' : 'Employee'}
                          </Badge>
                        </div>
                        {goal.description && <p className="text-sm text-gray-500 mt-1">{goal.description}</p>}
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-xs">{goal.status || 'Not Started'}</Badge>
                          <Badge variant="outline" className="text-xs">{goal.priority || 'Medium'}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No future goals</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Comments Tab */}
        <TabsContent value="comments" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  Employee Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {appraisal.comments?.employee || 'No comments from employee'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-500" />
                  Manager Comments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-violet-50 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">
                    {appraisal.comments?.manager || 'No comments from manager'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* CEO Review Tab */}
        <TabsContent value="ceo" className="mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500" />
                CEO Review & Comments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {appraisal.ceoReturnReason && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-1">Previous Return Reason:</p>
                  <p className="text-amber-700">{appraisal.ceoReturnReason}</p>
                </div>
              )}
              
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  CEO Comments / Feedback
                </label>
                <Textarea
                  placeholder="Add your comments or feedback on this appraisal..."
                  value={ceoComment}
                  onChange={(e) => handleCeoCommentChange(e.target.value)}
                  rows={6}
                  disabled={isFinalized}
                  className="resize-none"
                />
              </div>
              
              {!isFinalized && (
                <Button 
                  variant="outline" 
                  onClick={handleSaveCeoComment}
                  disabled={isSaving}
                >
                  Save Comment
                </Button>
              )}

              {appraisal.ceoApprovedAt && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm font-medium text-emerald-800">
                    ✓ Approved by CEO on {formatDate(appraisal.ceoApprovedAt)}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Finalize Modal */}
      <Dialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve & Finalize Appraisal</DialogTitle>
            <DialogDescription>
              Are you sure you want to approve and finalize this appraisal? This action will complete the review process.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Employee:</strong> {employee?.profile?.firstName} {employee?.profile?.lastName}
              </p>
              <p className="text-sm text-gray-600 mt-1">
                <strong>Final Score:</strong> {appraisal.scores?.finalWeightedScore?.toFixed(1) || 'N/A'}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinalizeModal(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleFinalApproval}
              disabled={isSaving}
            >
              {isSaving ? 'Finalizing...' : 'Approve & Finalize'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return to Supervisor Modal */}
      <Dialog open={showReturnModal} onOpenChange={setShowReturnModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Return to Supervisor</DialogTitle>
            <DialogDescription>
              Please provide a reason for returning this appraisal to the supervisor for revision.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter the reason for returning this appraisal..."
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReturnModal(false)}>Cancel</Button>
            <Button 
              className="bg-amber-600 hover:bg-amber-700" 
              onClick={handleReturnToSupervisor}
              disabled={isSaving || !returnReason.trim()}
            >
              {isSaving ? 'Returning...' : 'Return to Supervisor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
